// Arithmétique décimale exacte sur chaînes (les quantités ne sont jamais manipulées en flottants).
// Représentation : entier signé `m` (BigInt) et échelle `e`, valeur = m / 10^e.

export interface Decimal {
  m: bigint;
  e: number;
}

const MOTIF_DECIMAL = /^-?\d+(\.\d+)?$/;

export function estDecimal(valeur: string): boolean {
  return MOTIF_DECIMAL.test(valeur.trim());
}

export function lireDecimal(valeur: string): Decimal {
  const texte = valeur.trim();
  if (!MOTIF_DECIMAL.test(texte)) throw new Error(`Valeur décimale invalide : « ${valeur} »`);
  const negatif = texte.startsWith('-');
  const sansSigne = negatif ? texte.slice(1) : texte;
  const [entier = '0', fraction = ''] = sansSigne.split('.');
  const m = BigInt(entier + fraction) * (negatif ? -1n : 1n);
  return normaliser({ m, e: fraction.length });
}

function normaliser(d: Decimal): Decimal {
  let { m, e } = d;
  while (e > 0 && m % 10n === 0n) {
    m /= 10n;
    e -= 1;
  }
  return { m, e };
}

function aligner(a: Decimal, b: Decimal): [bigint, bigint, number] {
  const e = Math.max(a.e, b.e);
  return [a.m * 10n ** BigInt(e - a.e), b.m * 10n ** BigInt(e - b.e), e];
}

export function additionner(a: Decimal, b: Decimal): Decimal {
  const [x, y, e] = aligner(a, b);
  return normaliser({ m: x + y, e });
}

export function soustraire(a: Decimal, b: Decimal): Decimal {
  const [x, y, e] = aligner(a, b);
  return normaliser({ m: x - y, e });
}

export function multiplier(a: Decimal, b: Decimal): Decimal {
  return normaliser({ m: a.m * b.m, e: a.e + b.e });
}

/** Multiplie par 10^n (n peut être négatif) : opération exacte. */
export function decaler(a: Decimal, n: number): Decimal {
  if (n >= 0) return normaliser({ m: a.m * 10n ** BigInt(n), e: a.e });
  return normaliser({ m: a.m, e: a.e - n });
}

/** Division arrondie au plus proche (demi vers l'extérieur) à `echelle` décimales ; indique si elle est exacte. */
export function diviser(a: Decimal, b: Decimal, echelle: number): { valeur: Decimal; exacte: boolean } {
  if (b.m === 0n) throw new Error('Division par zéro');
  // a/b = (a.m / 10^a.e) / (b.m / 10^b.e) = a.m * 10^b.e / (b.m * 10^a.e)
  let numerateur = a.m * 10n ** BigInt(b.e) * 10n ** BigInt(echelle);
  let denominateur = b.m * 10n ** BigInt(a.e);
  if (denominateur < 0n) {
    numerateur = -numerateur;
    denominateur = -denominateur;
  }
  const quotient = numerateur / denominateur;
  const reste = numerateur % denominateur;
  let arrondi = quotient;
  const double = (reste < 0n ? -reste : reste) * 2n;
  if (double >= denominateur) arrondi += numerateur < 0n ? -1n : 1n;
  return { valeur: normaliser({ m: arrondi, e: echelle }), exacte: reste === 0n };
}

export function comparer(a: Decimal, b: Decimal): -1 | 0 | 1 {
  const [x, y] = aligner(a, b);
  return x < y ? -1 : x > y ? 1 : 0;
}

export function versChaine(d: Decimal): string {
  const negatif = d.m < 0n;
  const chiffres = (negatif ? -d.m : d.m).toString().padStart(d.e + 1, '0');
  const entier = d.e > 0 ? chiffres.slice(0, -d.e) : chiffres;
  const fraction = d.e > 0 ? chiffres.slice(-d.e) : '';
  return (negatif ? '-' : '') + entier + (fraction ? '.' + fraction : '');
}

/** Arrondit à `echelle` décimales (demi vers l'extérieur). */
export function arrondir(d: Decimal, echelle: number): Decimal {
  if (d.e <= echelle) return d;
  return diviser(d, { m: 1n, e: 0 }, echelle).valeur;
}

export function versNombre(d: Decimal): number {
  return Number(versChaine(d));
}
